import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import DefaultLayout from "../../layouts/DefaultLayout";
import { addNewBanner } from "../../redux/actions/bannerActions";
import { toast } from "react-toastify";

function AddBannerScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const [bannerTitle, setBannerTitle] = useState("");
  const [bannerTitleError, setBannerTitleError] = useState("");

  const [bannerImage, setBannerImage] = useState(null);
  const [bannerImageError, setBannerImageError] = useState("");
  const [isDragging, setIsDragging] = useState(false);

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const bannerCreate = useSelector((state) => state.createNewBanner);
  const { loadingBannerAdd, errorBannerAdd, successBannerAdd } = bannerCreate;

  const redirect = "/";
  useEffect(() => {
    if (!userInfo) {
      navigate(redirect);
    }
  }, [navigate, userInfo, dispatch]);

  useEffect(() => {
    if (successBannerAdd) {
      setBannerTitle("");
      setBannerTitleError("");
      setBannerImage(null);
      setBannerImageError("");
    }
  }, [successBannerAdd]);

  return (
    <DefaultLayout>
      <div>
        {/* Modern Breadcrumb */}
        <nav className="flex mb-6" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <a href="/dashboard" className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-primary transition-colors duration-200">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"></path>
                </svg>
                Dashboard
              </a>
            </li>
            <li>
              <div className="flex items-center">
                <svg className="w-5 h-5 text-secondary" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"></path>
                </svg>
                <a href="/banners" className="ml-1 text-sm font-medium text-primary hover:text-secondary md:ml-2 transition-colors duration-200">Banners</a>
              </div>
            </li>
            <li aria-current="page">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-secondary" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"></path>
                </svg>
                <span className="ml-1 text-sm font-medium text-secondary md:ml-2">Create Banner</span>
              </div>
            </li>
          </ol>
        </nav>

        {/* Header with Banner Image */}
        <div className="relative mb-8 rounded-xl overflow-hidden bg-gradient-to-r from-primary to-secondary shadow-lg" style={{backgroundImage: "url('/assets/background-auth-screen.png')", backgroundSize: "cover", backgroundPosition: "center"}}>
          <div className="absolute inset-0 bg-gradient-to-r from-primary/80 to-secondary/80"></div>
          <div className="relative z-10 px-6 py-8 md:px-10 md:py-12">
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">Create New Banner</h1>
            <p className="text-white/80 max-w-xl">Design eye-catching promotional banners to showcase your restaurant's special offers and featured items.</p>
          </div>
        </div>
        {/*  */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-6 py-5 border-b border-primary/10">
            <h2 className="font-semibold text-primary">Banner Information</h2>
            <p className="text-sm text-secondary mt-1">Fill in the details to create a new promotional banner</p>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 gap-6">
              {/* Banner Title */}
              <div>
                <label htmlFor="banner-title" className="block text-sm font-medium text-primary mb-1">
                  Banner Title <span className="text-secondary">*</span>
                </label>
                <input
                  id="banner-title"
                  type="text"
                  placeholder="Enter banner title"
                  value={bannerTitle}
                  onChange={(e) => setBannerTitle(e.target.value)}
                  className={`w-full px-4 py-3 rounded-lg border ${
                    bannerTitleError ? "border-red-500 focus:ring-red-500" : "border-secondary/30 focus:ring-primary"
                  } focus:border-transparent focus:outline-none focus:ring-2 transition-colors duration-200`}
                />
                {bannerTitleError && (
                  <p className="mt-1 text-sm text-red-500">{bannerTitleError}</p>
                )}
              </div>

              {/* Banner Image */}
              <div>
                <label htmlFor="banner-image" className="block text-sm font-medium text-primary mb-1">
                  Banner Image <span className="text-secondary">*</span>
                </label>
                <div
                  className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-lg ${
                    bannerImageError ? 'border-red-300' : isDragging ? 'border-primary' : 'border-secondary/30'
                  } ${isDragging ? 'bg-primary/5' : ''} hover:border-primary transition-colors duration-200`}
                  onDragEnter={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsDragging(true);
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsDragging(true);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsDragging(false);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsDragging(false);

                    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                      const file = e.dataTransfer.files[0];
                      // Check if file is an image
                      if (file.type.match('image.*')) {
                        setBannerImage(file);
                        setBannerImageError('');
                      } else {
                        setBannerImageError('Please upload an image file (PNG, JPG, GIF)');
                      }
                    }
                  }}
                >
                  <div className="space-y-1 text-center">
                    <svg className="mx-auto h-12 w-12 text-secondary" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true">
                      <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <div className="flex flex-col sm:flex-row items-center justify-center text-sm text-primary">
                      <label htmlFor="banner-image-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-secondary hover:text-secondary/80 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary">
                        <span>Upload a file</span>
                        <input
                          id="banner-image-upload"
                          name="banner-image-upload"
                          type="file"
                          accept="image/*"
                          className="sr-only"
                          onChange={(e) => {
                            if (e.target.files && e.target.files.length > 0) {
                              setBannerImage(e.target.files[0]);
                              setBannerImageError('');
                            }
                          }}
                        />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs text-secondary">
                      PNG, JPG, GIF up to 10MB
                    </p>
                  </div>
                </div>
                {bannerImageError && (
                  <p className="mt-1 text-sm text-red-500">{bannerImageError}</p>
                )}
                {bannerImage && (
                  <div className="mt-3 flex items-center">
                    <div className="w-16 h-16 border border-secondary/20 rounded-md overflow-hidden bg-gray-50 mr-3">
                      <img
                        src={URL.createObjectURL(bannerImage)}
                        alt="Banner preview"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-primary">{bannerImage.name}</p>
                      <p className="text-xs text-secondary">{(bannerImage.size / 1024).toFixed(2)} KB</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Form Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-primary/10 mt-4">
                <a
                  href="/banners"
                  className="px-4 py-2 border border-secondary/30 rounded-lg text-secondary bg-white hover:bg-secondary/5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200"
                >
                  Cancel
                </a>
                <button
                  disabled={loadingBannerAdd}
                  onClick={async () => {
                    var check = true;
                    setBannerTitleError("");
                    setBannerImageError("");

                    if (bannerTitle === "") {
                      setBannerTitleError("Banner title is required");
                      check = false;
                    }

                    if (!bannerImage) {
                      setBannerImageError("Banner image is required");
                      check = false;
                    }

                    if (check) {
                      await dispatch(
                        addNewBanner({
                          banner_title: bannerTitle,
                          banner_image: bannerImage,
                        })
                      ).then(() => {});
                    } else {
                      toast.error(
                        "Please fill in all required fields"
                      );
                    }
                  }}
                  className="px-4 py-2 border border-transparent rounded-lg shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200 flex items-center"
                >
                  {loadingBannerAdd ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Creating...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                      </svg>
                      Create Banner
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DefaultLayout>
  );
}

export default AddBannerScreen;
